import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useViewlogies } from 'context';
import { refresh, scheduleAtTime, useHashFragment } from 'utils';
import { isAuthenticated } from 'services/viewlogyAuth';
import Header from 'components/ClientHeader';
import ContactUs from 'components/ContactUs';
import Footer from 'components/Footer';
import FuneralStream from './FuneralStream';
import Navigation from './Navigation';
import MobileNavigation from './MobileNavigation';
import MobileFooter from './MobileFooter';
import GuestSection from './GuestSection';
import PlanningResource from './PlanningResource';
import GriefResource from './GriefResource';
import sharedMessages from 'messages';
import './viewlogy.scss';
import Donations from 'components/Donations';

const Viewlogy = props => {
    const {
        event,
        event: { firstName, lastName },
        videoLogin,
        streams,
        setIsLoggedIn,
        setIsPinLoggedIn,
        setStreams,
        setStreamIndex,
        isMobile,
    } = useViewlogies();

    const title = `${firstName} ${lastName} — ${sharedMessages.viewlogiesAllCaps}`;

    const eventStarted = new Date(streams[0].startTime).getTime() < new Date().getTime();

    const isSpecialEvent = streams.every(stream => stream.name === 'Special Event');

    const [activeTab, setActiveTab] = useState('stream');

    const [topbarHeight, setTopbarHeight] = useState(0);

    const tabs = [
        'stream',
        'guestList',
        // 'characterCloud',
        ...(!isSpecialEvent ? ['funeralGuide', 'griefSupport'] : []),
        // 'videoMemory',
        ...(isMobile ? ['helpCenter'] : []),
    ];

    const mobileScrollTab = (tab, extraOffset = 0) => {
        const element = document.getElementById(tab);
        if (element) {
            const topbarOffset = topbarHeight - 1 || 0;
            const y = element.getBoundingClientRect().top + window.pageYOffset - topbarOffset + extraOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const browserTabSelect = tab => {
        setActiveTab(tab);
        window.history.replaceState(null, '', `#${tab}`);
    };

    const handleScroll = () => {
        // const sections = document.querySelectorAll<HTMLElement>('[data-tab]');
        const sections = document.querySelectorAll('[data-tab]');
        const scrollY = window.scrollY + topbarHeight;

        for (const section of sections) {
            const rect = section.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            const bottom = top + section.offsetHeight;

            if (scrollY >= top && scrollY < bottom) {
                const tab = section.getAttribute('data-tab');
                if (tab && tab !== activeTab) {
                    setActiveTab(tab);
                    window.history.replaceState(null, '', `#${tab}`);
                }
                break; // No need to check further
            }
        }
    };

    const getInitialHashTab = (validTabs = tabs) => {
        const hash = window.location.hash.replace('#', '');
        return validTabs.includes(hash) ? hash : null;
    };

    const updateTabFromHash = () => {
        const initialTab = getInitialHashTab(tabs);
        if (initialTab) {
            setActiveTab(initialTab);
        }
    };

    // useHashFragment();

    //on page load, uses url hash to set active page
    useEffect(() => {
        if (isMobile) {
            const initialTab = getInitialHashTab();
            mobileScrollTab(initialTab, 250);
        } else {
            updateTabFromHash(); // run on mount
            window.addEventListener('hashchange', updateTabFromHash);
            return () => window.removeEventListener('hashchange', updateTabFromHash);
        }
    }, [isMobile, topbarHeight]);

    // sets activeTab to current section on scroll
    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });

        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab, topbarHeight]);

    /**
     * 1 - GA tracking
     * 2 - Video authentication
     * 3 - Set currently visible video
     */
    useEffect(() => {
        // set title manually before tracking page view
        document.title = `${firstName} ${lastName} — ${sharedMessages.viewlogiesAllCaps}`;

        if (props.pin) {
            videoLogin(props.pin); //triggers if pin is present in URL
        } else if (event.globalPin) {
            // check if authenticated
            const { globalPin: pin, id } = event;
            event.isLoggedIn = isAuthenticated({ pin, id, firstName });
            setIsLoggedIn(event.isLoggedIn);
            if (event.isLoggedIn) setIsPinLoggedIn(true);
        } else {
            setStreams(
                streams.map(stream => {
                    const { pin, id } = stream;
                    stream.isLoggedIn = isAuthenticated({ pin, id, firstName });
                    if (stream.isLoggedIn) setIsPinLoggedIn(true);
                    return stream;
                }),
            );
        }

        // set current stream index
        const now = new Date();
        for (const [index, stream] of streams.entries()) {
            // if stream is currently running
            if (new Date(stream.startTime) < now && now < new Date(stream.endTime) && stream.id !== 'TRIBUTE_VIDEO') {
                setStreamIndex(index);
                return;
            }
        }
    }, []);

    /**
     * schedule automatic refreshes
     */
    useEffect(() => {
        // if global PIN and logged in, schedule refresh for all event start times
        if (event.globalPin && event.isLoggedIn) {
            streams.forEach((stream, index) => {
                // switch index to index of stream
                scheduleAtTime(stream.startTime, [refresh, () => setStreamIndex(stream, index)]);
            });
        } else {
            // else schedule a refresh for each stream that is logged in
            streams.forEach((stream, index) => {
                if (stream.isLoggedIn) {
                    scheduleAtTime(stream.startTime, [refresh, () => setStreamIndex(stream, index)]);
                }
            });
        }
    }, [event.isLoggedIn, streams]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta property="og:title" content={title} />
            </Helmet>
            <main className="main-content centered viewlogy">
                {!isMobile ? (
                    <div className="viewlogy-layout">
                        <Navigation tabs={tabs} activeTab={activeTab} onTabSelect={tab => browserTabSelect(tab)} />
                        {/* TAB */}
                        <div className="tab-content">
                            {activeTab === 'stream' && <FuneralStream />}
                            {activeTab === 'guestList' && <GuestSection />}
                            {activeTab === 'funeralGuide' && <PlanningResource />}
                            {activeTab === 'griefSupport' && <GriefResource />}
                            {activeTab === 'helpCenter' && (
                                <div className="contact-us-wrapper">
                                    <ContactUs />
                                    {/* <div>** donations here</div> */}
                                    <Donations />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <MobileNavigation
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabSelect={tab => mobileScrollTab(tab)}
                            setTopbarHeight={setTopbarHeight}
                        />
                        <div style={{ height: topbarHeight }} />
                        <div className="viewlogy-mobile-layout">
                            <div id="stream" data-tab="stream">
                                <FuneralStream />
                            </div>
                            <div id="guestList" data-tab="guestList">
                                <GuestSection />
                            </div>
                            <div id="funeralGuide" data-tab="funeralGuide">
                                <PlanningResource />
                            </div>
                            <div id="griefSupport" data-tab="griefSupport">
                                <GriefResource />
                            </div>
                            <div id="helpCenter" data-tab="helpCenter">
                                <ContactUs />
                            </div>
                            <div className="mobile-donation-container">
                                <Donations />
                            </div>
                        </div>
                        <MobileFooter />
                    </>
                )}
            </main>
        </>
    );
};

export default Viewlogy;
